import { useCallback, useEffect, useState } from "react";

/**
 * CONVITE DE ENTREVISTA — a página que o CANDIDATO abre.
 *
 * É o link que o RH manda depois de agendar a entrevista na aba "Entrevistas":
 * /entrevista/<token>. Não tem login — o token é a credencial —, no mesmo
 * padrão de /triagem/<token>, /clima/<token> e /avaliacao/<token>.
 *
 * O QUE ISTO RESOLVE
 * ------------------
 * O convite ia solto no WhatsApp: "quinta 14h, na SERCAMP". Quem chegava não
 * sabia POR QUEM PERGUNTAR na recepção, ligava para o RH no portão, e a
 * entrevista começava atrasada. Por isso o bloco de "quem procurar" é o que
 * salta aos olhos aqui (convite-pub-destaque) — não é mais um detalhe no fim
 * do texto. Junto vêm o dia por extenso, o endereço com mapa, o que levar e um
 * telefone que disca com um toque.
 *
 * DECISÕES
 * --------
 * • A data por extenso vem PRONTA do servidor (`data_extenso`). Não se remonta
 *   aqui com `new Date(iso)`: o navegador do candidato interpretaria a data
 *   como UTC e um convite de 10/09 viraria 09/09 para quem está no Brasil.
 * • Nada de api.ts: aquele módulo manda cabeçalho de sessão do RH, e esta
 *   página não tem sessão. Fala direto por `fetch`, como a TriagemPublica.
 * • A resposta ("Confirmado" / "Não poderei") pode ser trocada — a API aceita
 *   nova resposta. Quem confirma e depois some é pior para o RH do que quem
 *   avisa; então a tela mantém o caminho aberto em vez de travar no primeiro
 *   clique.
 * • Só aparece o que o contrato público devolve. Observação interna do RH,
 *   status do processo e etapa seguinte não passam por aqui.
 */

/**
 * Quase tudo aqui é `string | null` porque é assim que chega mesmo: o servidor
 * devolve null (e não "") em campo não preenchido, e a entrevista pode estar
 * sem cargo, sem horário ou até sem dia — o RH apagou a data para remarcar
 * depois de o link já estar com o candidato. Declarar `string` faria o
 * TypeScript garantir uma coisa que o servidor não garante, e a tela quebraria
 * justamente no caso incompleto.
 */
interface LocalConvite {
  nome: string | null;
  endereco: string | null;
  complemento: string | null;
  referencia: string | null;
  mapa_url: string | null;
}

interface Convite {
  candidato: string;
  cargo: string | null;
  vaga: string | null;
  data: string | null;
  data_extenso: string | null;
  horario: string | null;
  etapa: string | null;
  entrevistador: string | null;
  local: LocalConvite;
  procurar_por: string | null;
  contato_nome: string | null;
  contato_telefone: string | null;
  orientacoes: string | null;
  documentos: string | null;
  resposta: "Confirmado" | "Nao poderei" | null;
  respondido_em: string | null;
  cancelada: boolean;
  encerrada: boolean;
  /** A entrevista está sem dia marcado (remarcação em andamento). */
  sem_data: boolean;
}

/** Casca comum das páginas por link (cabeçalho SERCAMP + cartão + rodapé). */
function Casca({ children }: { children: React.ReactNode }) {
  return (
    <div className="aval-publica">
      <div className="aval-header">
        <span className="aval-brand">SERCAMP</span>
        <span className="aval-brand-sub">Convite de entrevista</span>
      </div>
      <div className="aval-card">{children}</div>
      <p className="aval-rodape">
        RH SERCAMP · não pedimos dados bancários nem pagamento em nenhuma etapa
      </p>
    </div>
  );
}

/** Campo vazio no banco chega como "" — trata igual a ausente. */
function temTexto(v: string | null | undefined): v is string {
  return typeof v === "string" && v.trim() !== "";
}

/** Primeiro nome, para o cumprimento. Nome vazio não vira "Olá, !". */
function primeiroNome(nome: string): string {
  const parte = (nome ?? "").trim().split(/\s+/)[0];
  return parte || "tudo bem";
}

/**
 * Mapa: o RH pode colar o link exato (prédio com entrada difícil, ponto salvo
 * no Google). Quando não colou, monta a busca pelo endereço — melhor que
 * nenhum botão para quem vai de aplicativo.
 */
function linkDoMapa(local: LocalConvite): string | null {
  if (temTexto(local.mapa_url)) return local.mapa_url.trim();
  if (!temTexto(local.endereco)) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    local.endereco.trim(),
  )}`;
}

/** Só dígitos e "+" no href tel: — parênteses e traços quebram a discagem. */
function linkDoTelefone(telefone: string): string {
  return `tel:${telefone.replace(/[^\d+]/g, "")}`;
}

export default function ConvitePublico({ token }: { token: string }) {
  const [convite, setConvite] = useState<Convite | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  // Resposta do candidato.
  const [enviando, setEnviando] = useState(false);
  const [erroResposta, setErroResposta] = useState<string | null>(null);
  const [agradecimento, setAgradecimento] = useState<string | null>(null);
  // "Não poderei" abre um campo de recado antes de enviar: o motivo é o que
  // permite ao RH remarcar em vez de descartar o candidato.
  const [pedindoRecado, setPedindoRecado] = useState(false);
  const [recado, setRecado] = useState("");
  // Depois de responder a tela mostra o resumo; este estado reabre os botões.
  const [trocando, setTrocando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const res = await fetch(`/api/convite/entrevista/${encodeURIComponent(token)}`);
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error ?? "Convite indisponível.");
      setConvite(body as Convite);
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível abrir o convite.");
    } finally {
      setCarregando(false);
    }
  }, [token]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  async function responder(resposta: "Confirmado" | "Nao poderei") {
    setErroResposta(null);
    setEnviando(true);
    try {
      const res = await fetch(
        `/api/convite/entrevista/${encodeURIComponent(token)}/resposta`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ resposta, mensagem: recado.trim().slice(0, 500) }),
        },
      );
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error ?? "Não foi possível registrar a sua resposta.");
      // Atualiza a tela com o que foi respondido sem recarregar tudo: a pessoa
      // costuma estar em rede ruim, e uma segunda ida ao servidor pode falhar.
      setConvite((atual) =>
        atual ? { ...atual, resposta, respondido_em: new Date().toISOString() } : atual,
      );
      setAgradecimento(body?.mensagem ?? "Obrigado! O RH foi avisado.");
      setPedindoRecado(false);
      setTrocando(false);
      setRecado("");
    } catch (e) {
      setErroResposta(
        e instanceof Error ? e.message : "Não foi possível registrar a sua resposta.",
      );
    } finally {
      setEnviando(false);
    }
  }

  if (carregando) {
    return (
      <Casca>
        <div className="empty">Abrindo o convite…</div>
      </Casca>
    );
  }

  if (!convite) {
    return (
      <Casca>
        <div className="aval-estado">
          <h2>Convite indisponível</h2>
          <p>{erro ?? "Este link não está mais válido."}</p>
          <p>Fale com o RH da SERCAMP e peça um link novo.</p>
        </div>
      </Casca>
    );
  }

  if (convite.cancelada) {
    return (
      <Casca>
        <div className="aval-estado">
          <h2>Entrevista cancelada</h2>
          <p>Esta entrevista foi cancelada e não vai acontecer na data marcada.</p>
          <p>
            Se você acha que houve engano, fale com o RH da SERCAMP — o seu currículo continua
            conosco.
          </p>
        </div>
      </Casca>
    );
  }

  const mapa = linkDoMapa(convite.local);
  const cargoOuVaga = temTexto(convite.cargo)
    ? convite.cargo
    : temTexto(convite.vaga)
      ? (convite.vaga as string)
      : null;
  // Passada a data, o convite vira comprovante: mostra tudo, mas confirmar
  // presença já não faz sentido. Sem dia marcado também não: não há o que
  // confirmar enquanto o RH não remarcar.
  const podeResponder = !convite.encerrada && !convite.sem_data;
  const jaRespondeu = convite.resposta != null;

  return (
    <Casca>
      {/* O .convite-pub é quem dá o respiro entre os blocos: soltos dentro do
          cartão eles saem colados um no outro. */}
      <div className="convite-pub">
      <div className="convite-pub-hero">
        <h2>Olá, {primeiroNome(convite.candidato)}!</h2>
        <p>
          {cargoOuVaga ? (
            <>
              Sua entrevista para a vaga de <strong>{cargoOuVaga}</strong> está agendada.
            </>
          ) : (
            <>Sua entrevista na SERCAMP está agendada.</>
          )}
          {temTexto(convite.etapa) && <> Etapa: {convite.etapa}.</>}
        </p>
      </div>

      {convite.encerrada && (
        <div className="aval-estado">
          <h2>Data já passou</h2>
          <p>
            Este convite era para {convite.data_extenso}. Se você não conseguiu comparecer, fale com
            o RH da SERCAMP.
          </p>
        </div>
      )}

      {/* SEM DIA MARCADO: o RH apagou a data para remarcar e este link já
          estava com o candidato. Mostrar o cartão verde vazio faria a pessoa
          achar que perdeu a informação; o resto do convite (endereço, quem
          procurar) continua valendo e fica logo abaixo. */}
      {convite.sem_data ? (
        <div className="convite-pub-quando">
          <div className="convite-pub-dia">Data sendo remarcada</div>
          <div className="convite-pub-valor">
            O RH da SERCAMP vai avisar o novo dia e horário. Guarde este link — quando a data for
            definida, ela aparece aqui.
          </div>
        </div>
      ) : (
        <div className="convite-pub-quando">
          <div className="convite-pub-dia">{convite.data_extenso}</div>
          {/* "horário a combinar" é frase, não número: no corpo 36 do horário
              ela ocupa três linhas e rouba o destaque do dia. */}
          <div className={`convite-pub-hora${temTexto(convite.horario) ? "" : " sem-hora"}`}>
            {temTexto(convite.horario) ? convite.horario : "horário a combinar"}
          </div>
        </div>
      )}

      <div className="convite-pub-bloco">
        <div className="convite-pub-rotulo">Onde é</div>
        {temTexto(convite.local.nome) && (
          <div className="convite-pub-valor">
            <strong>{convite.local.nome}</strong>
          </div>
        )}
        {temTexto(convite.local.endereco) && (
          <div className="convite-pub-valor">{convite.local.endereco}</div>
        )}
        {temTexto(convite.local.complemento) && (
          <div className="convite-pub-valor">{convite.local.complemento}</div>
        )}
        {temTexto(convite.local.referencia) && (
          <div className="convite-pub-valor">Referência: {convite.local.referencia}</div>
        )}
        {mapa && (
          <a
            className="convite-pub-mapa btn-secondary"
            href={mapa}
            target="_blank"
            rel="noreferrer"
          >
            Ver no mapa
          </a>
        )}
      </div>

      {/* O BLOCO MAIS IMPORTANTE: é o que a pessoa vai reler no portão. */}
      {temTexto(convite.procurar_por) && (
        <div className="convite-pub-destaque">
          <div className="convite-pub-rotulo">Ao chegar, procure por</div>
          <div className="convite-pub-destaque-nome">{convite.procurar_por}</div>
          {temTexto(convite.contato_nome) && convite.contato_nome !== convite.procurar_por && (
            <div className="convite-pub-valor">Contato: {convite.contato_nome}</div>
          )}
          {temTexto(convite.contato_telefone) && (
            <a className="convite-pub-valor" href={linkDoTelefone(convite.contato_telefone)}>
              {convite.contato_telefone}
            </a>
          )}
        </div>
      )}

      {/* Sem "quem procurar" cadastrado, ao menos o telefone tem de aparecer. */}
      {!temTexto(convite.procurar_por) && temTexto(convite.contato_telefone) && (
        <div className="convite-pub-destaque">
          <div className="convite-pub-rotulo">Em caso de dúvida, ligue</div>
          <a
            className="convite-pub-destaque-nome"
            href={linkDoTelefone(convite.contato_telefone)}
          >
            {convite.contato_telefone}
          </a>
          {temTexto(convite.contato_nome) && (
            <div className="convite-pub-valor">Fale com {convite.contato_nome}</div>
          )}
        </div>
      )}

      {temTexto(convite.orientacoes) && (
        <div className="convite-pub-bloco">
          <div className="convite-pub-rotulo">Orientações</div>
          <div className="convite-pub-valor">{convite.orientacoes}</div>
        </div>
      )}

      {temTexto(convite.documentos) && (
        <div className="convite-pub-bloco">
          <div className="convite-pub-rotulo">O que levar</div>
          <div className="convite-pub-valor">{convite.documentos}</div>
        </div>
      )}

      {temTexto(convite.entrevistador) && (
        <div className="convite-pub-bloco">
          <div className="convite-pub-rotulo">Quem vai conversar com você</div>
          <div className="convite-pub-valor">{convite.entrevistador}</div>
        </div>
      )}

      {/* Resumo do que já foi respondido. Fica acima dos botões para quem abre
          o link de novo só para conferir se confirmou. */}
      {jaRespondeu && !trocando && (
        <div className="convite-pub-resposta">
          <div className="aval-ok-icon">✓</div>
          <p>
            {convite.resposta === "Confirmado"
              ? "Presença confirmada. Até lá!"
              : "Você avisou que não poderá comparecer. Obrigado por avisar."}
          </p>
          {agradecimento && <p>{agradecimento}</p>}
          {podeResponder && (
            <button type="button" className="btn-secondary" onClick={() => setTrocando(true)}>
              Mudou de ideia? Avise o RH
            </button>
          )}
        </div>
      )}

      {podeResponder && (!jaRespondeu || trocando) && (
        <div className="convite-pub-acoes">
          {!pedindoRecado ? (
            <>
              <button
                type="button"
                className="btn-primary"
                disabled={enviando}
                onClick={() => responder("Confirmado")}
              >
                {enviando ? "Enviando…" : "Confirmar presença"}
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={enviando}
                onClick={() => setPedindoRecado(true)}
              >
                Não poderei comparecer
              </button>
            </>
          ) : (
            <div className="convite-pub-recado">
              <label className="field field-wide">
                <span>Quer deixar um recado para o RH? (opcional)</span>
                <textarea
                  rows={3}
                  maxLength={500}
                  value={recado}
                  onChange={(e) => setRecado(e.target.value)}
                  placeholder="Ex.: tenho compromisso nesse horário, posso em outro dia"
                />
              </label>
              <button
                type="button"
                className="btn-primary"
                disabled={enviando}
                onClick={() => responder("Nao poderei")}
              >
                {enviando ? "Enviando…" : "Enviar aviso"}
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={enviando}
                onClick={() => {
                  setPedindoRecado(false);
                  setRecado("");
                }}
              >
                Voltar
              </button>
            </div>
          )}
        </div>
      )}

      {erroResposta && <div className="form-error">{erroResposta}</div>}

      {/* O .ics é servido pela rota pública: o navegador do celular entrega o
          arquivo ao calendário do aparelho. Sem dia marcado ele não existe —
          o botão sumiria em silêncio, e é melhor assim do que baixar um
          arquivo de erro. */}
      {!convite.sem_data && (
        <div className="convite-pub-acoes">
          <a
            className="btn-secondary"
            href={`/api/convite/entrevista/${encodeURIComponent(token)}/agenda.ics`}
          >
            Adicionar à agenda
          </a>
        </div>
      )}
      </div>
    </Casca>
  );
}
